import React, { FC, ChangeEvent } from 'react';
import { Color } from '../Theme';
import AccountMultipleIcon from 'mdi-react/AccountMultipleIcon';
import TrendingUpIcon from 'mdi-react/TrendingUpIcon';
import MapSearchIcon from 'mdi-react/MapSearchIcon';
import OpenInNewIcon from 'mdi-react/OpenInNewIcon';
import TextField from '@material-ui/core/TextField';
import { Field, Quote, Chip } from './CompanyFieldAndHelpers';
import { Grid, BasicInfoGrid, Column, Item, Spacer } from './CompanyGrid';
import { WWWCompanyDocument } from '../../../common/documents/WWWCompanyDocument';
import { companyEditState } from '../../context/companyEditStore';
import { useIntl, FormattedMessage } from 'react-intl';
import { CompanyEditButton } from './CompanyEditButton';
import { useCompanyDictionary } from './CompanyDictionary';
import { Header } from '../Header';
import { Help } from '../Help';
import { useFieldState, useScopedState } from '../../lib/state';
import styled from 'styled-components';
import { format, parseISO, isValid } from 'date-fns';
import { formatMinMax } from '../../common/constleUtils';
import { useCheckSelectedID } from '../../context/advancedSearchStore';
import { staticState } from '../../context/staticStore';
import { CompanyEditDialogContent } from './CompanyEditDialog';
import { CompanyEditSelect } from './CompanyEditSelect';

interface Props {
  company: WWWCompanyDocument;
  premium: boolean;
  loading: boolean;
  editable?: boolean;
  onEdit?: () => void;
}

const LargeQuote = styled(Quote)`
  min-height: 30.7px; // This is to make the height of the quote column the same as the turnover column
  white-space: pre;
  white-space: pre-wrap; /* css-3 */
  white-space: -moz-pre-wrap; /* Mozilla, since 1999 */
  white-space: -pre-wrap; /* Opera 4-6 */
  white-space: -o-pre-wrap; /* Opera 7 */
  word-wrap: break-word;

  word-break: break-all;
  word-break: break-word;
`;

export const CompanyBasicInfo: FC<Props> = ({ company, premium, loading, editable, onEdit }) => {
  const dictionary = useCompanyDictionary();

  const isLanguagesIDSelected = useCheckSelectedID('languages');

  const { name, email, description, dateEstablished, homepage, languages, numberOfEmployees, turnover, domicile, references } = company;

  const commonFieldProps = {
    premium,
    loading,
  };

  const numberOfEmployeesString = formatMinMax(numberOfEmployees);
  const turnoverString = formatMinMax(turnover);
  const domicileString = [domicile?.cityName || '', domicile?.provinceName || ''].filter((x) => !!x).join(', ');

  const dateEstablishedDate = parseISO(dateEstablished);

  // if established in previous year or later, show month
  const previousYear = new Date().getFullYear() - 1;
  const establishedMonthOrEmpty: string =
    company.dateEstablished && new Date(company.dateEstablished).getFullYear() >= previousYear
      ? format(parseISO(company.dateEstablished), 'M') + '/'
      : '';
  const dateEstablishedString = isValid(dateEstablishedDate)
    ? establishedMonthOrEmpty + format(parseISO(company.dateEstablished), 'yyyy')
    : '';
  const isPremiumLoading = premium && loading;

  return (
    <BasicInfoGrid>
      {!!onEdit && !!editable && <CompanyEditButton onClick={onEdit} />}
      <Column>
        <Item show={editable || description}>
          <Field label={dictionary.description} {...commonFieldProps}>
            <LargeQuote>{description}</LargeQuote>
          </Field>
        </Item>

        <Spacer show={editable || description} />

        <Grid>
          <Column>
            <Item>
              <Field label={dictionary.numberOfEmployees} {...commonFieldProps} large empty={!numberOfEmployeesString}>
                <AccountMultipleIcon color={Color.disabled} size={24} /> {numberOfEmployeesString}
              </Field>
            </Item>
          </Column>
          <Column>
            <Item>
              <Field label={dictionary.turnover} large {...commonFieldProps} empty={!turnoverString}>
                <TrendingUpIcon color={Color.disabled} size={24} /> {turnoverString}
              </Field>
            </Item>
          </Column>
        </Grid>

        <Spacer show={editable || description} />

        <Item show={editable || languages.length}>
          <Field label={dictionary.languages} {...commonFieldProps} chip empty={!languages.length}>
            {languages.map((l, i) => (
              <Chip key={l.languageID} selected={isLanguagesIDSelected(l.languageID)}>
                {l.nameTranslations['fi'] || l.name}
              </Chip>
            ))}
          </Field>
        </Item>
      </Column>

      <Column>
        <Grid>
          <Column>
            <Item>
              <Field label={dictionary.name} empty={!name} {...commonFieldProps}>
                {name}
              </Field>
            </Item>
          </Column>
          <Column>
            <Item>
              <Field label={dictionary.dateEstablished} empty={!dateEstablishedString} {...commonFieldProps}>
                {dateEstablishedString}
              </Field>
            </Item>
          </Column>
        </Grid>
        <Item show={domicileString}>
          <Field label={dictionary.domicile} {...commonFieldProps}>
            <span>{domicileString}</span>
            <a
              rel="noopener"
              aria-label="google maps"
              href={`https://www.google.com/maps/search/?api=1&query=${domicileString}`}
              target="_blank"
            >
              <MapSearchIcon size={18} color={Color.primary} />
            </a>
          </Field>
        </Item>
        <Item show={editable || isPremiumLoading || !premium || email}>
          <Field label={dictionary.email} premiumOnly {...commonFieldProps} empty={!email}>
            {email}
          </Field>
        </Item>
        <Item show={editable || homepage}>
          <Field label={dictionary.homepage} {...commonFieldProps} empty={!homepage}>
            <a href={'https://' + homepage} target="_blank">
              <span>{homepage}</span>
              <OpenInNewIcon size={18} color={Color.disabled} />
            </a>
          </Field>
        </Item>
      </Column>
    </BasicInfoGrid>
  );
};

export const CompanyBasicInfoEdit: CompanyEditDialogContent = ({ company, openSelect, closeSelect }) => {
  const intl = useIntl();

  const title = intl.formatMessage({ id: 'company.basicInfoTitle', defaultMessage: 'Perustiedot' });

  const dictionary = useCompanyDictionary();
  const { numberOfEmployees, set } = useScopedState(companyEditState, ['numberOfEmployees']);

  const { languages } = useScopedState(staticState, ['languages']);

  const descriptionField = useFieldState(companyEditState, 'description');
  const emailField = useFieldState(companyEditState, 'email');
  const homepageField = useFieldState(companyEditState, 'homepage');

  const handleNumberOfEmployeesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value) || 0;
    set({ numberOfEmployees: { min: value, max: value } });
  };

  return (
    <>
      <Header>{title}</Header>

      <TextField label={dictionary.description} variant="outlined" fullWidth multiline rows={4} {...descriptionField} />
      <Help>
        <FormattedMessage
          id="company.description.helpText"
          defaultMessage="Yritysesittely kiteyttää yrityksesi toiminnan ja tavoitteet muutamaan virkkeeseen"
        />
      </Help>

      <TextField
        label={dictionary.numberOfEmployees}
        variant="outlined"
        type="number"
        fullWidth
        InputProps={{ inputProps: { min: 0 } }}
        value={numberOfEmployees.min}
        onChange={handleNumberOfEmployeesChange}
      />

      <TextField label={dictionary.email} variant="outlined" type="email" fullWidth {...emailField} />
      <Help>
        <FormattedMessage id="company.email.helpText" defaultMessage="Yrityksesi sähköpostiosoite yhteydenottoja varten" />
      </Help>

      <TextField label={dictionary.homepage} variant="outlined" fullWidth {...homepageField} />

      <CompanyEditSelect
        parentTitle={title}
        title={dictionary.languages}
        path="languages"
        items={languages}
        onOpen={openSelect}
        onClose={closeSelect}
      />
    </>
  );
};
